import * as THREE from 'three';

/**
 * Checks if a THREE.js material has non-grayscale colors
 * @param material - The THREE.js material to check
 * @returns true if the material has non-grayscale colors, false otherwise
 */
const hasNonGrayscaleColors = (material: THREE.Material): boolean => {
  if (material instanceof THREE.MeshStandardMaterial && material.color) {
    const { r, g, b } = material.color;
    // Check if all RGB values are different (not grayscale)
    // Allow for small floating point differences
    const epsilon = 0.001;
    return Math.abs(r - g) > epsilon || Math.abs(g - b) > epsilon || Math.abs(r - b) > epsilon;
  }
  return false;
};

/**
 * Checks if a THREE.js material has texture maps
 * @param material - The THREE.js material to check
 * @returns true if the material has any texture maps, false otherwise
 */
const hasTextureMaps = (material: THREE.Material): boolean => {
  if (material instanceof THREE.MeshStandardMaterial) {
    return !!(
      material.map ||
      material.normalMap ||
      material.roughnessMap ||
      material.metalnessMap ||
      material.aoMap ||
      material.emissiveMap ||
      material.bumpMap ||
      material.displacementMap
    );
  }
  return false;
};

/**
 * Detects if a 3D model has original textures or meaningful colors
 * Returns true if the model has texture maps or non-grayscale colors
 * @param object - The THREE.js Object3D to analyze
 * @returns true if the model has original textures/colors, false otherwise
 */
export const hasOriginalTextures = (object: THREE.Object3D): boolean => {
  let hasTextures = false;

  object.traverse((child: THREE.Object3D) => {
    if (child instanceof THREE.Mesh && child.material) {
      const material = child.material as THREE.Material;

      // Check for texture maps
      if (hasTextureMaps(material)) {
        hasTextures = true;
        return; // Early exit once we find textures
      }

      // Check for non-grayscale colors
      if (hasNonGrayscaleColors(material)) {
        hasTextures = true;
        return; // Early exit once we find meaningful colors
      }
    }
  });

  return hasTextures;
};
